import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, RotateCcw, Trash2 } from "lucide-react";
import api, { formatApiError, paymentLabel } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal";

const STY = { paid: "bg-emerald-100 text-emerald-700", partially_paid: "bg-amber-100 text-amber-700", unpaid: "bg-zinc-200 text-zinc-600", overdue: "bg-red-100 text-red-700", draft: "bg-zinc-100 text-zinc-500", pending: "bg-amber-100 text-amber-700", failed: "bg-red-100 text-red-700", refunded: "bg-red-100 text-red-700", partially_refunded: "bg-red-100 text-red-700", voided: "bg-zinc-200 text-zinc-600" };

export default function Payments() {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const load = () => api.get("/payments").then(({ data }) => setPayments(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const visibleIds = payments.map((p) => p.id);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(() => (allSelected ? new Set() : new Set(visibleIds)));
  const clearSel = () => setSelected(new Set());

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      const { data } = await api.post("/payments/bulk-delete", { ids: [...selected] });
      toast.success(`${data.deleted} payment(s) deleted`);
      clearSel(); setConfirmOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setDeleting(false); }
  };

  const refund = async (p) => {
    const raw = window.prompt(`Refund amount for ${p.order_number} (max $${p.amount.toFixed(2)}). Leave blank for full refund:`, "");
    if (raw === null) return;
    const amount = raw.trim() ? Number(raw) : undefined;
    try { await api.post(`/payments/${p.id}/refund`, { amount }); toast.success("Refund processed"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="h-full overflow-y-auto p-6">
      <PageHeader title="Payments" subtitle={`${payments.length} transactions`} />

      {selected.size > 0 && (
        <div className="mb-4 bg-zinc-900 text-white rounded-2xl px-4 py-3 flex flex-wrap items-center gap-3" data-testid="pay-bulk-toolbar">
          <span className="font-semibold text-sm">{selected.size} selected</span>
          <button data-testid="pay-delete-selected" onClick={() => setConfirmOpen(true)} className="ml-auto h-9 px-3 rounded-lg bg-red-500 hover:bg-red-600 text-sm font-semibold flex items-center gap-1.5"><Trash2 size={15} />Delete Selected ({selected.size})</button>
        </div>
      )}

      <div className="bg-white border border-zinc-200/70 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-100 text-left text-zinc-400 uppercase text-xs tracking-wider">
            <tr>
              <th className="p-4 w-10"><input type="checkbox" data-testid="pay-select-all" checked={allSelected} onChange={toggleAll} className="h-4 w-4 accent-brand cursor-pointer" /></th>
              <th className="p-4">Payment</th><th className="p-4">Invoice</th><th className="p-4 hidden md:table-cell">Date</th><th className="p-4">Method</th><th className="p-4">Amount</th><th className="p-4">Status</th><th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p.id} data-testid={`pay-row-${p.id}`} className={`border-b border-zinc-100 last:border-0 ${selected.has(p.id) ? "bg-brand-soft/40" : ""}`}>
                <td className="p-4"><input type="checkbox" data-testid={`pay-select-${p.id}`} checked={selected.has(p.id)} onChange={() => toggleSel(p.id)} className="h-4 w-4 accent-brand cursor-pointer" /></td>
                <td className="p-4 font-mono text-xs">{p.id.slice(0, 8)}</td>
                <td className="p-4"><button onClick={() => navigate(`/invoices/${p.invoice_id}/edit`)} data-testid={`pay-invoice-${p.id}`} className="font-semibold text-brand hover:underline">{p.invoice_number}</button></td>
                <td className="p-4 hidden md:table-cell text-zinc-400">{p.created_at.slice(0, 10)}</td>
                <td className="p-4">{paymentLabel(p.method)}</td>
                <td className="p-4 font-semibold">${p.amount.toFixed(2)}{p.refunded_amount > 0 && <span className="text-red-500 text-xs"> (−${p.refunded_amount.toFixed(2)})</span>}</td>
                <td className="p-4"><span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${STY[p.status] || "bg-zinc-100"}`}>{p.status.replace("_", " ")}</span></td>
                <td className="p-4 text-right">{["paid"].includes(p.status) && <button onClick={() => refund(p)} data-testid={`pay-refund-${p.id}`} className="h-9 px-3 rounded-lg border border-zinc-200 flex items-center gap-1 text-xs font-semibold ml-auto active:scale-95"><RotateCcw size={14} />Refund</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {payments.length === 0 && <div className="p-12 text-center text-zinc-400 flex flex-col items-center gap-2"><CreditCard size={30} />No payments yet.</div>}
      </div>

      <ConfirmDeleteModal open={confirmOpen} count={selected.size} noun="payment" busy={deleting} onCancel={() => setConfirmOpen(false)} onConfirm={confirmDelete} testid="pay-confirm-delete" />
    </div>
  );
}
